import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import apiService from "../services/api";
import AudioButton from "../components/AudioButton";
import PageShell from "../components/PageShell";

/** A single lesson: caption, ASCII diagram, Teaching section, and audio. */
export default function LessonDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    apiService
      .getLesson(slug)
      .then((data) => {
        setLesson(data);
        setStatus("ready");
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          navigate("/signin", {
            state: { from: `/articles/lesson/${slug}` },
            replace: true,
          });
          return;
        }
        setStatus("error");
      });
  }, [slug, navigate]);

  return (
    <PageShell>
      <Link
        to={lesson ? `/articles/topic/${lesson.topic_slug}` : "/articles"}
        className="text-sm text-sky-400 transition hover:text-sky-300"
      >
        ← {lesson ? lesson.topic_name : "Back"}
      </Link>

      {status === "loading" && <p className="mt-6 text-slate-500">Loading…</p>}
      {status === "error" && (
        <p className="mt-6 text-rose-400">Lesson not found.</p>
      )}

      {status === "ready" && lesson && (
        <article className="mt-4">
          <h1 className="text-3xl font-bold tracking-tight text-slate-100">
            {lesson.title}
          </h1>
          <p className="mt-3 text-slate-400">{lesson.caption}</p>

          <div className="mt-6">
            <AudioButton
              slug={lesson.slug}
              hasAudio={lesson.has_audio}
              audioUrl={lesson.audio_url}
            />
          </div>

          {lesson.diagram && (
            <div className="mt-8 overflow-x-auto rounded-xl border border-slate-800 bg-slate-900/60 p-4">
              {/* Box-drawing characters need the extra contrast to stay legible. */}
              <pre className="whitespace-pre font-mono text-xs leading-snug text-slate-200">
                {lesson.diagram}
              </pre>
            </div>
          )}

          {lesson.teaching && (
            <section className="mt-10">
              {/* "Teaching" heading only for diagram lessons; stories render as
                    plain article body. */}
              {lesson.diagram && (
                <h2 className="mb-3 text-xl font-semibold text-slate-100">
                  Teaching
                </h2>
              )}
              <div className="prose prose-invert max-w-none lg:prose-lg prose-p:text-slate-300 prose-li:text-slate-300 prose-headings:text-slate-100 prose-strong:text-slate-100 prose-a:text-sky-400 prose-code:text-sky-300 prose-pre:bg-slate-900/60 prose-pre:text-slate-200 prose-blockquote:text-slate-400 prose-th:text-slate-200 prose-td:text-slate-300">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {lesson.teaching}
                </ReactMarkdown>
              </div>
            </section>
          )}
        </article>
      )}
    </PageShell>
  );
}
